import * as React from "react";

import { RouteComponentProps, withRouter } from "react-router";
import { LoadingViewState, RouterView } from "../RouterView";
import {
  AssignRewardToUserRequest,
  CasinoGame,
  GameIntegration,
  Prepaid,
  RewardTemplate,
  RewardType,
  TemplateItem,
  AdminSecuritySettings,
} from "../../http/protocol";
import { Form, FormListener } from "../form/Form";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { Validators } from "../form/Validators";
import { SelectInput } from "../form/SelectInput";
import { SettingsService } from "../../services/SettingsService";
import { RewardsConfigService } from "../../services/RewardsConfigService";
import { CasinoGameService } from "../../services/CasinoGameService";
import { AddCasinoGames } from "../casino/AddCasinoGames";
import { t } from "../../i18n";
import { InternalSettingsService } from "../../services/InternalSettingsService";
import { CurrencyRateService } from "../../services/CurrencyRateService";
import { CurrencyEuroValue } from "../account/CurrencyEuroValue";
import UserRewardVerification, { RequestFeedback, VerificationType } from "../user/UserRewardVerification";
import { LargeAmountConfirmationDialog } from "../dialog/LargeAmountConfirmationDialog";
import { SupervisorValidationModal } from "../dialog/SupervisorValidationModal";
import { LargeAmountConfirmationHandler } from "../util/largeAmountConfirmation";
import { findPrepaidByAmount, Hub88PrepaidTarget, hub88PrepaidKey, loadHub88Prepaids, prepaidAmount, prepaidsByStake } from "./hub88Prepaids";

interface State extends LoadingViewState {
  request?: AssignRewardToUserRequest;
  templates?: RewardTemplate[];
  validationMap?: any;
  selectedGame?: CasinoGame;
  showAddGamesDialog?: boolean;
  loading?: boolean;
  freeSpinsIntegrations?: GameIntegration[];
  verificationModal: boolean;
  currencyToEuroRate: number | undefined;
  rewardTemplate: RewardTemplate;
  requestFeedback: boolean;
  isTemplateActive: boolean;
  templateItems: TemplateItem[];
  showConfirmationDialog: boolean;
  euroAmountForConfirmation?: number;
  largeAmountThreshold?: number;
  supervisorValidationMessage?: string;
  effectiveGame?: CasinoGame;
  loadedGameId?: number;
  templateGame?: CasinoGame;
  templateGameId?: number;
  hub88Prepaids?: Prepaid[];
  hub88PrepaidsKey?: string;
  hub88Unavailable?: boolean;
}

export class AssignRewardToUserPage extends RouterView<RouteComponentProps, {}> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = { loading: true };
  }
  getTitle(): string {
    return "Assign/Award Reward";
  }
  loadContent(): Promise<any> {
    return Promise.resolve();
  }
  renderContent() {
    return (
      <div className="wrapper wrapper-content animated fadeInRight">
        <AssignRewardToUser />
      </div>
    );
  }
}
export class AssignRewardToUser
  extends React.Component<{ userId?: number; currencyEuroValueEnabled?: boolean }, State>
  implements FormListener<AssignRewardToUserRequest>
{
  service: RewardsConfigService;
  settingsService: SettingsService;
  gameService: CasinoGameService;
  internalSettingsService: InternalSettingsService;
  currencyRateService: CurrencyRateService;
  rewardsConfigService: RewardsConfigService;
  private confirmationHandler: LargeAmountConfirmationHandler;
  private userCancelled: boolean = false;

  constructor(props: {}) {
    super(props);
    this.service = getContainer().getRewardsConfigService();
    this.settingsService = getContainer().getSettingsService();
    this.gameService = getContainer().getCasinoGameService();
    this.internalSettingsService = getContainer().getInternalSettingsService();
    this.currencyRateService = getContainer().getCurrencyRateService();
    this.rewardsConfigService = getContainer().getRewardsConfigService();
    this.confirmationHandler = new LargeAmountConfirmationHandler();
    this.toggleVerificationModal = this.toggleVerificationModal.bind(this);
    this.requestFeedback = this.requestFeedback.bind(this);
    let request = {};
    if (typeof this.props.userId != "undefined") {
      request = { userId: this.props.userId };
    }
    this.state = {
      request: request,
      templates: [],
      templateItems: [],
      validationMap: {},
      loading: true,
      verificationModal: false,
      currencyToEuroRate: undefined,
      rewardTemplate: {},
      requestFeedback: false,
      isTemplateActive: true,
      showConfirmationDialog: false,
    };
  }

  componentDidMount() {
    this.confirmationHandler.loadSecuritySettings();
    this.service.getBetbyTemplates().then((response) => {
      this.setState({ templateItems: response ?? [] });
    });
    return this.service.list({ limit: 1000, offset: 0, orderBy: "name" }).then((list) => {
      let gi: GameIntegration[] = [];

      this.internalSettingsService.getSettings().then((settings) => {
        if (settings.freeSpinsEnabledIntegrations != null) {
          settings.freeSpinsEnabledIntegrations?.split(",").forEach((integrationName) => {
            Object.values(GameIntegration).forEach((enumName) => {
              if (enumName === integrationName) {
                gi.push(enumName);
              }
            });
          });
        }
      });

      this.setState({ templates: list.items, loading: false, freeSpinsIntegrations: gi });
    });
  }

  componentWillUnmount() {
    this.confirmationHandler.reset();
  }

  componentDidUpdate() {
    this.syncEffectiveGame();
    this.syncTemplateGame();
    this.syncHub88Prepaids();
    this.syncHub88LockedRequestFields();
  }

  currentTemplate(): RewardTemplate | undefined {
    return this.state.templates?.find((t) => t.id == this.state.request?.rewardTemplateId);
  }

  /** The game this assignment targets: the manually-selected one, else the template's default. */
  resolvedGameId(): number | undefined {
    return this.state.request?.gameId ?? this.currentTemplate()?.defaultCasinoGameId;
  }

  /** Load the resolved game (keyed by id) so its integration is known for Hub88 free-spins handling. */
  syncEffectiveGame() {
    const template = this.currentTemplate();
    const gameId = template?.rewardType == RewardType.freespins ? this.resolvedGameId() : undefined;
    if (!gameId) {
      if (this.state.loadedGameId != null) {
        this.setState({ loadedGameId: undefined, effectiveGame: undefined });
      }
      return;
    }
    if (gameId == this.state.loadedGameId) {
      return;
    }
    this.setState({ loadedGameId: gameId });
    this.gameService
      .getEntity("" + gameId)
      .then((game) => this.setState({ effectiveGame: game }))
      .catch((error) => console.error("Failed to load game for reward assignment", error));
  }

  /** Load the template's DEFAULT game (keyed by id) so the help text can name it even after a different game is picked. */
  syncTemplateGame() {
    const template = this.currentTemplate();
    const gameId = template?.rewardType == RewardType.freespins ? template.defaultCasinoGameId : undefined;
    if (!gameId) {
      if (this.state.templateGameId != null) {
        this.setState({ templateGameId: undefined, templateGame: undefined });
      }
      return;
    }
    if (gameId == this.state.templateGameId) {
      return;
    }
    this.setState({ templateGameId: gameId });
    this.gameService
      .getEntity("" + gameId)
      .then((game) => this.setState({ templateGame: game }))
      .catch((error) => console.error("Failed to load reward template game", error));
  }

  /** A human-readable label for the template's default game, falling back to its id while the name loads. */
  templateGameLabel(): string | undefined {
    const gameId = this.currentTemplate()?.defaultCasinoGameId;
    if (!gameId) {
      return undefined;
    }
    const game = this.state.templateGame;
    return game && game.id == gameId ? game.id + " - " + game.name : "" + gameId;
  }

  isHub88Freespins(): boolean {
    return this.currentTemplate()?.rewardType == RewardType.freespins && this.state.effectiveGame?.integration == GameIntegration.HUB88;
  }

  /** A Hub88 free-spins template — the reward must stay on a Hub88 game (its prepaid stake is Hub88-specific). */
  isHub88Template(): boolean {
    return this.currentTemplate()?.rewardType == RewardType.freespins && this.state.templateGame?.integration == GameIntegration.HUB88;
  }

  /** Integrations selectable in the game picker: locked to Hub88 for a Hub88 template, else the free-spins-enabled set. */
  gameIntegrations(): GameIntegration[] | undefined {
    return this.isHub88Template() ? [GameIntegration.HUB88] : this.state.freeSpinsIntegrations;
  }

  assignCurrency(): string | undefined {
    return this.state.request?.currencyCode || this.currentTemplate()?.currencyCode;
  }

  hub88Target(): Hub88PrepaidTarget | undefined {
    if (!this.isHub88Freespins()) {
      return undefined;
    }
    const gameCode = this.state.effectiveGame?.externalId;
    const currency = this.assignCurrency();
    return gameCode && currency ? { gameCode, currency } : undefined;
  }

  syncHub88Prepaids() {
    const target = this.hub88Target();
    const key = hub88PrepaidKey(target);
    if (key == this.state.hub88PrepaidsKey) {
      return;
    }
    if (!target) {
      this.setState({ hub88PrepaidsKey: undefined, hub88Prepaids: undefined, hub88Unavailable: false });
      return;
    }
    this.setState({ hub88PrepaidsKey: key, hub88Unavailable: false });
    // Ignore a response if the game/currency moved on while it was in flight — otherwise a slow earlier
    // fetch could overwrite the prepaids for the currency now selected.
    loadHub88Prepaids(this.service, target).then((result) => {
      if (this.state.hub88PrepaidsKey === key) {
        this.setState({ hub88Prepaids: result.prepaids, hub88Unavailable: result.unavailable });
      }
    });
  }

  /** The prepaid currently backing request.amount, matched on the integer bet_value to avoid float equality. */
  selectedPrepaid(): Prepaid | undefined {
    return findPrepaidByAmount(this.state.hub88Prepaids, this.state.request?.amount ?? undefined);
  }

  onSelectPrepaid(prepaidUuid: string) {
    const prepaid = this.state.hub88Prepaids?.find((p) => p.prepaid_uuid == prepaidUuid);
    this.setState({ request: { ...this.state.request, amount: prepaid ? prepaidAmount(prepaid) : undefined } });
  }

  /**
   * A Hub88 free-spins reward's currency is locked to the template (and the amount too, when the
   * template fixes it) and shown read-only. Mirror those values into the request so the award POST
   * and the confirmation dialog carry them, rather than the empty currency a read-only span leaves.
   */
  syncHub88LockedRequestFields() {
    if (!this.isHub88Freespins()) {
      return;
    }
    const template = this.currentTemplate();
    const patch: Partial<AssignRewardToUserRequest> = {};
    // Currency is locked to the template for Hub88 free-spins.
    const lockedCurrency = template?.currencyCode;
    if (lockedCurrency != null && this.state.request?.currencyCode !== lockedCurrency) {
      patch.currencyCode = lockedCurrency;
    }
    // The stake must be a prepaid on the CHOSEN game (grids differ per game). Once its prepaids load, default to
    // the template amount when this game offers it, and drop a stale amount a game switch left behind so the
    // admin re-picks from the new grid.
    const prepaids = this.state.hub88Prepaids;
    if (prepaids != null) {
      const current = this.state.request?.amount ?? undefined;
      if (current != null && findPrepaidByAmount(prepaids, current) == null) {
        patch.amount = undefined;
      } else if (current == null && template?.defaultAmount != null && findPrepaidByAmount(prepaids, template.defaultAmount ?? undefined) != null) {
        patch.amount = template.defaultAmount;
      }
    }
    if (Object.keys(patch).length > 0) {
      this.setState({ request: { ...this.state.request, ...patch } });
    }
  }

  /**
   * A Hub88 free-spins reward must be assigned at a real prepaid stake. When the template fixes the amount it
   * is used as-is; otherwise a prepaid must be picked here (sets request.amount) before the reward can be awarded.
   */
  isHub88AmountMissing(currentTemplate?: RewardTemplate): boolean {
    return this.isHub88Freespins() && currentTemplate?.defaultAmount == null && this.state.request?.amount == null;
  }

  toggleAddShowAddGames() {
    this.setState({ showAddGamesDialog: !this.state.showAddGamesDialog });
  }

  async getExchangeRate(currency: string) {
    this.currencyRateService.getCryptoAndFiatToEuroExchangeRates(currency).then((rate) => {
      this.setState({ currencyToEuroRate: rate });
    });
  }

  async checkThresholdAndProceed(): Promise<any> {
    const { request } = this.state;

    if (!request?.rewardTemplateId) {
      return;
    }

    this.userCancelled = false;

    try {
      const rewardTemplate = await this.rewardsConfigService.getReward(request.rewardTemplateId);
      this.setState({ rewardTemplate });

      const currency = request.currencyCode || rewardTemplate.currencyCode;
      let amount = request.amount;

      if (!amount) {
        amount = rewardTemplate.defaultAmount;
      }

      if (rewardTemplate.rewardType === RewardType.freespins && request.nrOfFreeSpins && amount) {
        amount = amount * request.nrOfFreeSpins;
      }

      if (!amount || !currency) {
        return this.proceedToVerification(currency as string);
      }

      const result = await this.confirmationHandler.checkThreshold(amount, currency as string);

      if (result.requiresConfirmation) {
        this.setState({
          euroAmountForConfirmation: result.euroAmount,
          showConfirmationDialog: true,
          largeAmountThreshold: this.confirmationHandler.getThreshold(),
        });

        try {
          await this.confirmationHandler.waitForConfirmation();
        } catch (error: any) {
          if (error?.message === "USER_CANCELLED") {
            return;
          }
          throw error;
        }
      }

      return this.proceedToVerification(currency as string);
    } catch (error: any) {
      console.error("Error checking threshold:", error);
      this.confirmationHandler.resetSubmissionFlag();
      throw error;
    }
  }

  private proceedToVerification(currency?: string): Promise<void> {
    if (!currency) {
      console.error("No currency provided for verification");
      return Promise.reject({ message: "Currency is required for verification" });
    }

    const code = this.confirmationHandler.trimmedCurrency(currency);
    return this.currencyRateService
      .getCryptoAndFiatToEuroExchangeRates(code)
      .then((rate) => {
        this.setState({ currencyToEuroRate: rate, verificationModal: true });
      })
      .catch((error) => {
        console.error(`Failed to get exchange rate for ${code}`, error);
        throw { message: `Failed to fetch exchange rate for ${code}. Please try again.` };
      });
  }

  onSubmit() {
    return this.checkThresholdAndProceed();
  }

  handleConfirmLargeAmount = () => {
    this.setState({
      showConfirmationDialog: false,
      euroAmountForConfirmation: undefined,
    });
    this.userCancelled = false;
    this.confirmationHandler.confirmLargeAmount();
  };

  handleCancelLargeAmount = () => {
    this.setState({
      showConfirmationDialog: false,
      euroAmountForConfirmation: undefined,
    });
    this.userCancelled = true;
    this.confirmationHandler.cancelLargeAmount();
  };

  toggleVerificationModal() {
    this.setState({ verificationModal: false });
    this.confirmationHandler.resetSubmissionFlag();
  }

  requestFeedback(type: string) {
    if (type == "open") {
      this.setState({ requestFeedback: true });
    }
    if (type == "close") {
      this.setState({ requestFeedback: false });
      this.setState({ request: { ...this.state.request, nrOfFreeSpins: undefined, amount: undefined, currencyCode: undefined } });
    }
  }

  formDidUpdate(formModel: AssignRewardToUserRequest, validationMap?: any) {
    let rewardTemplate = this.state.templates?.find((t) => t.id == formModel.rewardTemplateId);
    let templateItems = this.state.templateItems;
    if (rewardTemplate?.rewardType == RewardType.freebet) {
      if (templateItems.length) {
        this.state.templateItems.forEach((template) => {
          if (template.id == rewardTemplate?.templateId) {
            if (template.is_active) {
              this.setState({ isTemplateActive: true });
            } else {
              this.setState({ isTemplateActive: false });
            }
          }
        });
      } else {
        this.setState({ isTemplateActive: false });
      }
    }
    this.setState({ request: formModel, validationMap: validationMap });
  }
  selectGame(game: CasinoGame) {
    this.setState({ request: { ...this.state.request, gameId: game.id }, showAddGamesDialog: false, selectedGame: game });
  }

  getVerificationModal() {
    return (
      <UserRewardVerification
        type={VerificationType.user_reward}
        userRewardRequest={this.state.request}
        userRewardTemplate={this.state.rewardTemplate}
        toggleVerificationModal={this.toggleVerificationModal}
        isVerificationModalOpen={this.state.verificationModal}
        euroExchangeRate={this.state.currencyToEuroRate}
        requestFeedback={this.requestFeedback}
        onSupervisorRequired={(message) => this.setState({ supervisorValidationMessage: message })}
      />
    );
  }

  getSuccessModal() {
    if (this.state.requestFeedback) {
      return (
        <RequestFeedback
          type={VerificationType.user_reward}
          requestFeedback={this.requestFeedback}
          isOpen={this.state.requestFeedback}
        />
      );
    }
  }

  render() {
    if (!this.state.request || this.state.loading) {
      return <span />;
    }
    let currentTemplate;
    if (this.state.request?.rewardTemplateId && this.state.templates) {
      currentTemplate = this.state.templates.find((t) => t.id == this.state.request?.rewardTemplateId);
    }
    let targetCurrency = currentTemplate?.currencyCode;
    if (this.state.request.currencyCode && this.state.request.currencyCode.length > 0) {
      targetCurrency = this.state.request.currencyCode;
    }

    let displayAmount = this.state.request.amount || currentTemplate?.defaultAmount || 0;
    if (currentTemplate?.rewardType === RewardType.freespins && this.state.request.nrOfFreeSpins) {
      displayAmount = displayAmount * this.state.request.nrOfFreeSpins;
    }

    return (
      <div>
        {this.getVerificationModal()}
        {this.getSuccessModal()}

        <SupervisorValidationModal
          show={!!this.state.supervisorValidationMessage}
          message={this.state.supervisorValidationMessage || ""}
          onClose={() => this.setState({ supervisorValidationMessage: undefined })}
        />

        {this.state.showConfirmationDialog && targetCurrency && (
          <LargeAmountConfirmationDialog
            show={this.state.showConfirmationDialog}
            originalAmount={displayAmount}
            originalCurrency={targetCurrency}
            euroAmount={this.state.euroAmountForConfirmation || 0}
            onConfirm={this.handleConfirmLargeAmount}
            onCancel={this.handleCancelLargeAmount}
          />
        )}

        <Form
          className="form form-horizontal"
          validationMap={this.state.validationMap}
          modelObject={this.state.request}
          formListener={this}
        >
          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                <div className="ibox-content">
                  <FormGroup className="form-group">
                    <label className="col-sm-4 control-label">{t(`label.rewardTemplate`)}</label>
                    <div className="col-sm-10">
                      <SelectInput
                        model="rewardTemplateId"
                        optional={false}
                        values={this.state.templates}
                        optionModelValue={(e) => e.id}
                        displayValue={(e) => e.name + " - " + e.id + " (" + e.currencyCode + ")"}
                        className="form-control"
                      />
                      {currentTemplate?.rewardType == RewardType.freebet && !this.state.isTemplateActive && (
                        <span className={"help text-danger"}>
                          Current sportsbook template for this config is not active. Active sportsbook template in betby backoffice
                        </span>
                      )}
                    </div>
                  </FormGroup>
                  <div className="hr-line-dashed" />
                  {!this.props.userId && (
                    <>
                      <FormGroup className="form-group">
                        <label className="col-sm-4 control-label">{t(`label.userId`)}</label>
                        <div className="col-sm-10">
                          <FormInput
                            model="userId"
                            validators={[Validators.required()]}
                            type="integer"
                            className="form-control"
                          />
                          <span className={"help"}>{t(`helpText.userIdReward`)}</span>
                        </div>
                      </FormGroup>
                      <div className="hr-line-dashed" />
                    </>
                  )}
                  {currentTemplate?.rewardType == RewardType.freespins && (
                    <>
                      <div className="form-group">
                        <label className="col-sm-2 control-label">{t(`label.game`)}</label>
                        <div className="col-sm-10">
                          {!this.state.request.gameId && (
                            <span
                              className={"alert alert-info"}
                              style={{ display: "block" }}
                            >
                              {t(`alertInfo.selectGame`)}
                            </span>
                          )}
                          {this.state.request.gameId && (
                            <span className={"form-control"}>
                              {this.state.selectedGame?.id} - {this.state.selectedGame?.name} - {this.state.selectedGame?.integration}
                            </span>
                          )}
                          <a
                            style={{ marginTop: "5px" }}
                            className={"btn btn-primary"}
                            onClick={() => this.toggleAddShowAddGames()}
                          >
                            {t(`button.selectGame`)}
                          </a>
                          <span className={"help"}>
                            {t(
                              `helpText.selectGame`,
                              { game: this.templateGameLabel() ?? "" },
                              "Select the game for the free spins (reward template game: {{game}}).",
                            )}
                          </span>
                        </div>
                      </div>
                      <FormGroup className="form-group">
                        <label className="col-sm-4 control-label">{t(`label.nrOfFreespins`)}</label>
                        <div className="col-sm-10">
                          <FormInput
                            model="nrOfFreeSpins"
                            type="integer"
                            className="form-control"
                          />
                          <span className={"help"}>
                            {t(`helpText.nrOfFreespinsFirstPart`)} ({currentTemplate.defaultNrOfFreeSpins}) {t(`helpText.nrOfFreespinsSecondPart`)}
                          </span>
                        </div>
                      </FormGroup>
                    </>
                  )}
                  {currentTemplate != null && (
                    <>
                      <FormGroup className="form-group">
                        <label className="col-sm-4 control-label">{t(`label.currencyCode`)}</label>
                        <div className="col-sm-10">
                          {this.isHub88Freespins() ? (
                            <>
                              <span className="form-control">{this.assignCurrency()}</span>
                              <span className={"help"}>
                                {t(
                                  `helpText.hub88CurrencyLocked`,
                                  undefined,
                                  "Locked to the template currency for Hub88 free-spins — the prepaid stake is denominated in it.",
                                )}
                              </span>
                            </>
                          ) : (
                            <>
                              <FormInput
                                model="currencyCode"
                                type="text"
                                className="form-control"
                              />
                              <span className={"help"}>
                                {t(`helpText.currencyCode`)} ({currentTemplate.currencyCode})
                              </span>
                            </>
                          )}
                        </div>
                      </FormGroup>
                      <div className="hr-line-dashed" />
                      {this.isHub88Freespins() ? (
                        <FormGroup className="form-group">
                          <label className="col-sm-4 control-label">{t(`label.amount`)}</label>
                          <div className="col-sm-10">
                            {this.state.hub88Unavailable && (
                              <span
                                className={"alert alert-warning"}
                                style={{ display: "block" }}
                              >
                                {t(`alertInfo.hub88PrepaidsUnavailable`, undefined, "Could not load Hub88 prepaids — try again later.")}
                              </span>
                            )}
                            <select
                              className="form-control"
                              data-testid="hub88-prepaid-select"
                              value={this.selectedPrepaid()?.prepaid_uuid ?? ""}
                              onChange={(e) => this.onSelectPrepaid(e.target.value)}
                            >
                              <option value="">{t(`label.hub88PrepaidSelect`, undefined, "Select a prepaid…")}</option>
                              {prepaidsByStake(this.state.hub88Prepaids)
                                .map((p) => (
                                  <option
                                    key={p.prepaid_uuid}
                                    value={p.prepaid_uuid}
                                  >
                                    {prepaidAmount(p)} {this.assignCurrency()}
                                  </option>
                                ))}
                            </select>
                            <span className={"help"}>
                              {t(
                                `helpText.hub88PrepaidAssign`,
                                undefined,
                                "The per-spin stake must be a real Hub88 prepaid value — select one to set the amount.",
                              )}
                            </span>
                          </div>
                        </FormGroup>
                      ) : (
                        <FormGroup className="form-group">
                          <label className="col-sm-4 control-label">{t(`label.amount`)}</label>
                          <div className="col-sm-10">
                            <FormInput
                              model="amount"
                              validators={[Validators.number()]}
                              type="float"
                              className="form-control"
                            />
                            {/* <span className={"help"}>The Amount in ({targetCurrency}) to reward, if left empty {currentTemplate.defaultAmount} {targetCurrency} will be rewarded</span> */}
                            {this.props.currencyEuroValueEnabled && (
                              <CurrencyEuroValue
                                sourceCurrency={targetCurrency}
                                sourceAmount={this.state.request?.amount}
                              />
                            )}
                            <div className={"alert alert-warning guide"}>
                              <span>{t(`helpText.amountReward`, { targetCurrency: targetCurrency, defaultAmount: currentTemplate.defaultAmount })}</span>
                              <br />
                              <span>CASH rewards: the default amount to reward to the player</span>
                              <br />
                              <span>
                                FREESPINS rewards: the bet amount for every spin. Not all integrations support this (will be ignored if not supported by the
                                game integration)
                              </span>
                              <br />
                              <span>FREEBET rewards: total amount. Exchanged value will be awarded if currency is not supported by integration.</span>
                            </div>
                          </div>
                        </FormGroup>
                      )}
                    </>
                  )}
                  <div className="hr-line-dashed" />
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                <FormSubmitError />
                <div className="form-group">
                  <div className="col-sm-12">
                    <Submit
                      disabled={currentTemplate?.rewardType == RewardType.freebet ? !this.state.isTemplateActive : this.isHub88AmountMissing(currentTemplate)}
                      className="btn  btn-primary"
                    >
                      {t(`button.award`)}
                    </Submit>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
        <AddCasinoGames
          // Re-mount when the selectable integrations change: AddCasinoGames captures includeIntegrations into
          // its game query only in its constructor, so a Hub88 template must force a fresh mount to take effect.
          key={"assign-games-" + (this.gameIntegrations() ?? []).join(",")}
          title="Select Casino Game"
          onlyFreeSpinSupported={true}
          includeIntegrations={this.gameIntegrations()}
          showAddGames={this.state.showAddGamesDialog}
          toggleShowAddGames={() => this.toggleAddShowAddGames()}
          addGame={(game: CasinoGame) => {
            this.selectGame(game);
          }}
          removeGame={() => {}}
          addedGames={[]}
        />
      </div>
    );
  }
}

export default withRouter(AssignRewardToUserPage);

/* this.service.getBetbyTemplates().then((templates) => {
    let template = templates.find((template) => template.id == response.templateId)
    if(template !== undefined){
        this.setState({isTemplateActive: template.is_active ? true : false})
    }
}) */
